'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import classes from './NeumoVideo.module.scss';
import { IconPlayerPlay, IconX } from '@tabler/icons-react';

type NeumoVideoProps = {
  title: string;
  description?: string;
  videoUrl: string;
  posterUrl?: string;
  videoAlt: string;
  badge?: string;
  id?: string;
};

export const NeumoVideo = ({ data }: { data: NeumoVideoProps }) => {
  const { title, description, videoUrl, posterUrl, videoAlt, badge, id } = data;
  const [isOpen, setIsOpen] = useState(false);
  const [canPortal, setCanPortal] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => setCanPortal(true), []);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  // ESC + lock scroll
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const modal = isOpen ? (
    <div className={classes.modalOverlay} role="dialog" aria-modal="true" aria-label={videoAlt} onClick={close}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <button className={classes.modalClose} onClick={close} aria-label="Fermer la vidéo" type="button">
          <IconX size={20} />
        </button>

        <div className={classes.modalVideoContainer}>
          <video
            ref={videoRef}
            className={classes.modalVideo}
            controls
            autoPlay
            playsInline
            preload="auto"
          >
            <source src={videoUrl} type="video/mp4" />
            Votre navigateur ne supporte pas la lecture de vidéos.
          </video>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <section className={classes.section} aria-labelledby="video-title">
      <div className={classes.container}>
        <div className={classes.header}>
          {badge && (
            <div className={classes.badgeWrapper}>
              <span className={classes.badge}>{badge}</span>
            </div>
          )}
          <h2 id={id} className={classes.title}>{title}</h2>
          {description && <p className={classes.description}>{description}</p>}
        </div>

        <div className={classes.videoWrapper}>
          {/* ✅ Light version: poster card only */}
          <div className={classes.videoContainer} role="button" tabIndex={0} onClick={open}
               onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open()}
               aria-label="Ouvrir la vidéo">
            {posterUrl ? (
              <Image
                src={posterUrl}
                alt={videoAlt}
                fill
                sizes="(max-width: 64em) 100vw, 66vw"
                className={classes.posterNextImage}
                priority={false}
              />
            ) : (
              <div className={classes.fallbackPoster} aria-hidden="true" />
            )}

            <div className={classes.posterOverlay}>
              <button type="button" className={classes.playButton} onClick={open} aria-label="Lire la vidéo">
                <IconPlayerPlay size={48} stroke={2} />
              </button>
            </div>
          </div>

          <div className={classes.info}>
            <div className={classes.infoCard}>
              <h3 className={classes.infoTitle}>Comment ça fonctionne</h3>
              <ul className={classes.infoList}>
                <li>
                  <span className={classes.infoNumber}>1</span>
                  <div>
                    <strong>Vous partagez une phrase</strong>
                    <span>Votre besoin, votre offre ou votre message clé</span>
                  </div>
                </li>
                <li>
                  <span className={classes.infoNumber}>2</span>
                  <div>
                    <strong>L'IA génère le contenu</strong>
                    <span>Article premium, posts sociaux, emails ciblés</span>
                  </div>
                </li>
                <li>
                  <span className={classes.infoNumber}>3</span>
                  <div>
                    <strong>Publication automatique</strong>
                    <span>Votre site, réseaux sociaux et CRM synchronisés</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Portal modal */}
      {canPortal && modal ? createPortal(modal, document.body) : null}
    </section>
  );
};
